// Modal do Quiz sobre Homeopatia
// Componente principal que exibe perguntas, coleta respostas e fornece feedback educativo

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { QuizSystem } from '../../utils/QuizSystem';
import type { QuizQuestion } from '../../utils/QuizSystem';
import { useAuth } from '../../contexts/AuthContext';

interface QuizModalProps {
  isOpen: boolean;
  onClose: () => void;
  memberId: string;
  onQuizComplete?: (summary: any) => void;
}

interface QuizState {
  currentQuestion: QuizQuestion | null;
  selectedAnswer: number | null;
  showResult: boolean;
  isCorrect: boolean;
  pointsEarned: number;
  explanation: string;
  isLoading: boolean;
  error: string | null;
}

export const QuizModal: React.FC<QuizModalProps> = ({
  isOpen,
  onClose,
  memberId,
  onQuizComplete
}) => {
  const { user } = useAuth();
  const [quizState, setQuizState] = useState<QuizState>({
    currentQuestion: null,
    selectedAnswer: null,
    showResult: false,
    isCorrect: false,
    pointsEarned: 0,
    explanation: '',
    isLoading: true,
    error: null
  });

  const [progress, setProgress] = useState({ completed: 0, total: 3 });
  const quizSystemRef = useRef(QuizSystem.getInstance());
  const onCloseRef = useRef(onClose);
  const onQuizCompleteRef = useRef(onQuizComplete);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    onQuizCompleteRef.current = onQuizComplete;
  }, [onQuizComplete]);

  // Carrega próxima pergunta
  const loadNextQuestion = useCallback(async () => {
    const quizSystem = quizSystemRef.current;
    if (isFetchingRef.current) {
      return;
    }
    isFetchingRef.current = true;
    try {
      setQuizState(prev => ({ ...prev, isLoading: true, error: null }));
      
      // Verificar e renovar perguntas se necessário
      await quizSystem.checkAndRenewQuestions(memberId);
      
      const nextQuestion = await quizSystem.getNextQuestion(memberId);
      const currentProgress = quizSystem.getQuizProgress(memberId);
      const isQuizCompleted = quizSystem.isQuizCompletedToday(memberId);
      
      setProgress(currentProgress);
      
      if (!nextQuestion) {
        // Verifica se o quiz foi completado (3 perguntas respondidas)
        if (isQuizCompleted) {
          // Quiz completo - exibe mensagem de parabéns
          setQuizState(prev => ({
            ...prev,
            error: 'Parabéns, você respondeu suas perguntas diárias. Volte amanhã para responder mais.',
            isLoading: false
          }));
          return;
        } else if (currentProgress && currentProgress.completed > 0) {
          // Quiz parcialmente completo mas sem próxima pergunta
          const summary = await quizSystem.getDailySummary(memberId);
          onQuizCompleteRef.current?.(summary);
          onCloseRef.current?.();
        } else {
          // Nenhuma pergunta disponível
          setQuizState(prev => ({
            ...prev,
            error: 'Nenhuma pergunta disponível. Tente novamente mais tarde.',
            isLoading: false
          }));
        }
        return;
      }

      setQuizState(prev => ({
        ...prev,
        currentQuestion: nextQuestion,
        selectedAnswer: null,
        showResult: false,
        isLoading: false,
        error: null
      }));
    } catch (_error) {
      console.error('Erro ao carregar pergunta:', _error);
      setQuizState(prev => ({
        ...prev,
        error: 'Erro ao carregar pergunta. Tente novamente.',
        isLoading: false
      }));
    } finally {
      isFetchingRef.current = false;
    }
  }, [memberId]);

  // Submete resposta
  const submitAnswer = async () => {
    if (quizState.selectedAnswer === null || !quizState.currentQuestion) return;
    if (!user?.id) {
      setQuizState(prev => ({
        ...prev,
        error: 'Usuário não autenticado. Faça login novamente.'
      }));
      return;
    }

    try {
      const quizSystem = quizSystemRef.current;
      const result = await quizSystem.submitQuizAnswer(
        memberId,
        quizState.currentQuestion.id,
        quizState.selectedAnswer,
        user.id
      );

      setQuizState(prev => ({
        ...prev,
        showResult: true,
        isCorrect: result.isCorrect,
        pointsEarned: result.pointsEarned,
        explanation: result.explanation || ''
      }));

      // Atualiza progresso
      const newProgress = quizSystemRef.current.getQuizProgress(memberId);
      setProgress(newProgress);

    } catch (error) {
      console.error('❌ [QuizModal] Erro ao submeter resposta:', error);
      setQuizState(prev => ({
        ...prev,
        error: 'Erro ao submeter resposta. Tente novamente.'
      }));
    }
  };

  // Próxima pergunta
  const nextQuestion = async () => {
    // Verificar se completou todas as perguntas antes de carregar a próxima
    const quizSystem = quizSystemRef.current;
    const currentProgress = quizSystem.getQuizProgress(memberId);
    
    if (currentProgress.completed >= currentProgress.total) {
      // Quiz completo - chamar callback antes de fechar
      const summary = await quizSystem.getDailySummary(memberId);
      onQuizComplete?.(summary);
    }
    
    void loadNextQuestion();
  };

  // Inicializa quiz quando modal abre
  useEffect(() => {
    if (isOpen) {
      void loadNextQuestion();
    }
  }, [isOpen, loadNextQuestion]);

  // Seleciona resposta
  const selectAnswer = (answerIndex: number) => {
    if (quizState.showResult) return;
    setQuizState(prev => ({ ...prev, selectedAnswer: answerIndex }));
  };

  // Fecha modal
  const handleClose = () => {
    setQuizState({
      currentQuestion: null,
      selectedAnswer: null,
      showResult: false,
      isCorrect: false,
      pointsEarned: 0,
      explanation: '',
      isLoading: true,
      error: null
    });
    onCloseRef.current?.();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-xl">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">Quiz de Homeopatia</h2>
              <p className="text-blue-100 mt-1">
                Pergunta {progress.completed + 1} de {progress.total}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Barra de progresso */}
          <div className="mt-4 bg-blue-800 rounded-full h-2">
            <div 
              className="bg-white rounded-full h-2 transition-all duration-300"
              style={{ width: `${(progress.completed / progress.total) * 100}%` }}
            />
          </div>
        </div>

        {/* Conteúdo */}
        <div className="p-6">
          {quizState.isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Carregando pergunta...</p>
            </div>
          ) : quizState.error ? (
            <div className="text-center py-8">
              {/* Verifica se é mensagem de parabéns (quiz completo) */}
              {quizState.error.includes('Parabéns') ? (
                <>
                  <div className="text-green-500 mb-4">
                    <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-gray-600 mb-4">{quizState.error}</p>
                </>
              ) : (
                <>
                  <div className="text-red-500 mb-4">
                    <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-gray-600 mb-4">{quizState.error}</p>
                  <button
                    onClick={loadNextQuestion}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Tentar Novamente
                  </button>
                </>
              )}
            </div>
          ) : quizState.currentQuestion ? (
            <>
              {/* Pergunta */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <span className={`
                    px-3 py-1 rounded-full text-sm font-medium
                    ${quizState.currentQuestion.difficulty === 'fácil' ? 'bg-green-100 text-green-800' :
                      quizState.currentQuestion.difficulty === 'médio' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'}
                  `}>
                    {quizState.currentQuestion.difficulty}
                  </span>
                  <span className="text-sm text-gray-500">
                    Pergunta {progress.completed + 1} de {progress.total}
                  </span>
                </div>
                
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  {quizState.currentQuestion.question}
                </h3>
              </div>

              {/* Opções de resposta */}
              <div className="space-y-3 mb-6">
                {quizState.currentQuestion?.options?.map((option, index) => {
                  const isSelected = quizState.selectedAnswer === index;
                  const isCorrect = index === quizState.currentQuestion!.correctAnswer;
                  const showResult = quizState.showResult;
                  
                  let buttonClass = 'w-full p-4 text-left border-2 rounded-lg transition-all duration-200 ';
                  
                  if (showResult) {
                    if (isCorrect) {
                      buttonClass += 'border-green-500 bg-green-50 text-green-800';
                    } else if (isSelected && !isCorrect) {
                      buttonClass += 'border-red-500 bg-red-50 text-red-800';
                    } else {
                      buttonClass += 'border-gray-200 bg-gray-50 text-gray-600';
                    }
                  } else {
                    if (isSelected) {
                      buttonClass += 'border-blue-500 bg-blue-50 text-blue-800';
                    } else {
                      buttonClass += 'border-gray-200 hover:border-gray-300 hover:bg-gray-50';
                    }
                  }

                  return (
                    <button
                      key={index}
                      onClick={() => !quizState.showResult && selectAnswer(index)}
                      disabled={quizState.showResult}
                      className={buttonClass}
                    >
                      <div className="flex items-center">
                        <span className="font-medium mr-3">
                          {String.fromCharCode(65 + index)}.
                        </span>
                        <span>{option}</span>
                        {showResult && isCorrect && (
                          <svg className="w-5 h-5 text-green-600 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                        {showResult && isSelected && !isCorrect && (
                          <svg className="w-5 h-5 text-red-600 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Resultado e explicação */}
              {quizState.showResult && (
                <div className={`
                  p-4 rounded-lg mb-6
                  ${quizState.isCorrect ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}
                `}>
                  <div className="flex items-center mb-2">
                    {quizState.isCorrect ? (
                      <svg className="w-6 h-6 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-6 h-6 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    )}
                    <span className={`font-semibold ${quizState.isCorrect ? 'text-green-800' : 'text-red-800'}`}>
                      {quizState.isCorrect ? 'Correto!' : 'Incorreto'}
                    </span>
                    {quizState.pointsEarned > 0 && (
                      <span className="ml-auto bg-green-600 text-white px-2 py-1 rounded-full text-sm font-bold">
                        +{quizState.pointsEarned} pontos
                      </span>
                    )}
                  </div>
                  {quizState.explanation && (
                    <p className={`text-sm ${quizState.isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                      {quizState.explanation}
                    </p>
                  )}
                </div>
              )}

              {/* Botões de ação */}
              <div className="flex justify-between">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Fechar
                </button>
                
                {!quizState.showResult ? (
                  <button
                    onClick={submitAnswer}
                    disabled={quizState.selectedAnswer === null}
                    className={`
                      px-6 py-2 rounded-lg font-medium transition-colors
                      ${quizState.selectedAnswer !== null
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }
                    `}
                  >
                    Confirmar Resposta
                  </button>
                ) : (
                  <button
                    onClick={nextQuestion}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    {progress.completed + 1 >= progress.total ? 'Finalizar Quiz' : 'Proxima Pergunta'}
                  </button>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default QuizModal;